import { ThemeEvents } from '@theme/events';

const APPSTLE_SYNC_EVENTS = [
  'AppstleSubscription:SubscriptionWidget:widgetInitialised',
  'AppstleSubscription:SubscriptionWidget:SellingPlanSelected',
  'AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected',
  'AppstleSubscription:SubscriptionWidget:sellingPlanChanged',
  'AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated',
];

/**
 * Syncs Appstle subscription widget prices into theme price displays.
 */
class AppstlePriceSync {
  /** @type {AbortController} */
  #abortController = new AbortController();

  init() {
    const { signal } = this.#abortController;

    for (const eventName of APPSTLE_SYNC_EVENTS) {
      document.addEventListener(eventName, this.#scheduleSync, { signal });
    }

    document.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.name === 'selling_plan') {
          this.#scheduleSync();
        }
      },
      { signal }
    );

    for (const section of document.querySelectorAll('.shopify-section')) {
      section.addEventListener(ThemeEvents.variantUpdate, this.#scheduleSync, { signal });
    }

    this.#scheduleSync();
  }

  #scheduleSync = () => {
    requestAnimationFrame(() => this.sync());
  };

  sync() {
    const widget = document.querySelector('.appstle_sub_widget');
    if (!widget) return;

    const prices = this.#readPricesFromWidget(widget);
    if (!prices?.salePrice) return;

    const productPrice = document.querySelector('product-price');
    const productPriceContainer = productPrice?.querySelector('[ref="priceContainer"]');
    if (productPriceContainer) {
      this.#applyToPriceContainer(productPriceContainer, prices);
    }

    const unitPriceEl = productPrice?.querySelector('small.unit-price');
    if (unitPriceEl) {
      if (prices.unitPrice) {
        this.#setUnitPrice(unitPriceEl, prices.unitPrice);
      } else {
        this.#restoreUnitPrice(unitPriceEl);
      }
    }

    const stickyBar = document.querySelector('sticky-add-to-cart');
    if (!stickyBar) return;

    const stickyPriceContainer = stickyBar.querySelector('.sticky-add-to-cart__price [ref="priceContainer"]');
    if (stickyPriceContainer) {
      this.#applyToPriceContainer(stickyPriceContainer, prices);
    }

    this.#syncStickyButtonPrices(stickyBar, prices);
  }

  /**
   * @param {Element} widget
   * @returns {{ isSubscription: boolean, salePrice: string, comparePrice: string | null, saveBadge: string | null, unitPrice: string | null } | null}
   */
  #readPricesFromWidget(widget) {
    const selected = widget.querySelector('input[name="selling_plan"]:checked');
    const isSubscription = selected instanceof HTMLInputElement ? Boolean(selected?.value) : false;

    const activeOption = isSubscription
      ? widget.querySelector('.appstle-active-option, .appstle_selected_background')
      : widget.querySelector('.appstle_one_time_details_wrapper');

    if (!activeOption) return null;

    const salePriceEl = activeOption.querySelector('.appstle_subscription_amount:not(.sr-only)');
    const salePrice = salePriceEl?.textContent?.trim() ?? '';

    const unitPriceSelector = isSubscription ? '.appstle_selling_plan_unit_price' : '.appstle_one_time_unit_price';
    const unitPrice = activeOption.querySelector(unitPriceSelector)?.textContent?.trim() ?? null;

    if (!isSubscription) {
      return { isSubscription: false, salePrice, comparePrice: null, saveBadge: null, unitPrice };
    }

    const comparePrice =
      activeOption.querySelector('.appstle_subscription_compare_amount:not(.sr-only)')?.textContent?.trim() ?? null;
    const saveBadge = activeOption.querySelector('.appstle-save-badge')?.textContent?.trim() ?? null;

    return { isSubscription: true, salePrice, comparePrice, saveBadge, unitPrice };
  }

  /**
   * @param {Element} container
   * @param {{ isSubscription: boolean, salePrice: string, comparePrice: string | null, saveBadge: string | null }} prices
   */
  #applyToPriceContainer(container, prices) {
    const salePriceEl = container.querySelector('.price');
    if (!salePriceEl) return;

    if (prices.isSubscription) {
      this.#applySubscriptionPrices(container, salePriceEl, prices);
      return;
    }

    this.#clearAppstleMarkup(container, salePriceEl);
    salePriceEl.textContent = this.#formatPriceText(prices.salePrice);
  }

  /**
   * @param {Element} container
   * @param {Element} salePriceEl
   * @param {{ salePrice: string, comparePrice: string | null, saveBadge: string | null }} prices
   */
  #applySubscriptionPrices(container, salePriceEl, prices) {
    if (salePriceEl instanceof HTMLElement && !salePriceEl.dataset.appstleOriginalPrice) {
      salePriceEl.dataset.appstleOriginalPrice = salePriceEl.textContent.trim();
    }

    const compareText =
      prices.comparePrice || (salePriceEl instanceof HTMLElement ? salePriceEl.dataset.appstleOriginalPrice : null);
    salePriceEl.textContent = this.#formatPriceText(compareText ?? '');
    salePriceEl.setAttribute('data-appstle-price-modified', 'true');

    let finalPriceEl = container.querySelector('.appstle_subscription_final_price');
    if (!finalPriceEl) {
      finalPriceEl = document.createElement('span');
      finalPriceEl.className = 'appstle_subscription_final_price transcy-money';
      container.appendChild(finalPriceEl);
    }
    finalPriceEl.textContent = prices.salePrice;

    if (prices.saveBadge) {
      let saveBadgeEl = container.querySelector('.appstle_subscription_save');
      if (!saveBadgeEl) {
        saveBadgeEl = document.createElement('span');
        saveBadgeEl.className = 'appstle_subscription_save';
        container.appendChild(saveBadgeEl);
      }
      saveBadgeEl.textContent = prices.saveBadge;
    } else {
      container.querySelector('.appstle_subscription_save')?.remove();
    }
  }

  /**
   * @param {Element} container
   * @param {Element} salePriceEl
   */
  #clearAppstleMarkup(container, salePriceEl) {
    container.querySelector('.appstle_subscription_final_price')?.remove();
    container.querySelector('.appstle_subscription_save')?.remove();
    salePriceEl.removeAttribute('data-appstle-price-modified');

    if (salePriceEl instanceof HTMLElement && salePriceEl.dataset.appstleOriginalPrice) {
      salePriceEl.textContent = salePriceEl.dataset.appstleOriginalPrice;
      delete salePriceEl.dataset.appstleOriginalPrice;
    }
  }

  /**
   * @param {Element} stickyBar
   * @param {{ isSubscription: boolean, salePrice: string, comparePrice: string | null, saveBadge: string | null }} prices
   */
  #syncStickyButtonPrices(stickyBar, prices) {
    const buttonPrice = stickyBar.querySelector('[ref="buttonPrice"]');
    const buttonCompare = stickyBar.querySelector('[ref="buttonComparePrice"]');
    const buttonPriceWrapper = stickyBar.querySelector('.sticky-add-to-cart__button-price');

    if (prices.isSubscription) {
      if (buttonCompare instanceof HTMLElement && !buttonCompare.dataset.appstleOriginalPrice) {
        buttonCompare.dataset.appstleOriginalPrice = buttonCompare.textContent.trim();
      }

      if (buttonCompare && prices.comparePrice) {
        buttonCompare.textContent = prices.comparePrice;

        if (buttonCompare instanceof HTMLElement) {
          buttonCompare.style.display = '';
        }
      }
      if (buttonPrice) {
        buttonPrice.textContent = prices.salePrice;
      }
      if (buttonPriceWrapper) {
        this.#applySubscriptionPrices(
          buttonPriceWrapper,
          buttonPrice || buttonPriceWrapper.querySelector('.price') || buttonPriceWrapper,
          prices
        );
      }
      return;
    }

    if (buttonPriceWrapper) {
      const priceEl = buttonPriceWrapper.querySelector('.price') || buttonPrice;
      if (priceEl instanceof HTMLElement) {
        this.#clearAppstleMarkup(buttonPriceWrapper, priceEl);
      } else {
        buttonPriceWrapper.querySelector('.appstle_subscription_final_price')?.remove();
        buttonPriceWrapper.querySelector('.appstle_subscription_save')?.remove();
      }
    }

    if (buttonPrice instanceof HTMLElement) {
      buttonPrice.removeAttribute('data-appstle-price-modified');
      delete buttonPrice.dataset.appstleOriginalPrice;
    }

    this.#restoreStickyButtonFromProductPrice(stickyBar);
  }

  /**
   * Restores sticky button prices from the main product price after leaving subscription mode.
   * @param {Element} stickyBar
   */
  #restoreStickyButtonFromProductPrice(stickyBar) {
    const productPriceContainer = document.querySelector('product-price [ref="priceContainer"]');
    const buttonPrice = stickyBar.querySelector('[ref="buttonPrice"]');
    const buttonCompare = stickyBar.querySelector('[ref="buttonComparePrice"]');

    if (!productPriceContainer) {
      return;
    }

    const salePriceEl = productPriceContainer.querySelector('.price');
    const comparePriceEl = productPriceContainer.querySelector('.compare-at-price');

    if (buttonPrice && salePriceEl) {
      buttonPrice.textContent = salePriceEl.textContent.trim();
    }

    if (!(buttonCompare instanceof HTMLElement)) {
      return;
    }

    const compareText = comparePriceEl?.textContent?.trim();
    if (compareText) {
      buttonCompare.textContent = compareText;
      buttonCompare.style.display = '';
    } else {
      buttonCompare.style.display = 'none';
    }

    delete buttonCompare.dataset.appstleOriginalPrice;
  }

  /**
   * @param {Element} unitPriceEl
   * @returns {string}
   */
  #getUnitPriceVisibleText(unitPriceEl) {
    const hiddenLabel = unitPriceEl.querySelector('.visually-hidden');
    const clone = unitPriceEl.cloneNode(true);

    if (clone instanceof HTMLElement) {
      clone.querySelector('.visually-hidden')?.remove();
    }

    const visibleText = clone.textContent?.trim() ?? '';

    if (visibleText) return visibleText;

    return unitPriceEl.textContent?.replace(hiddenLabel?.textContent ?? '', '').trim() ?? '';
  }

  /**
   * Updates unit price text while preserving the visually-hidden accessibility label.
   * @param {Element} unitPriceEl
   * @param {string} value
   */
  #setUnitPrice(unitPriceEl, value) {
    if (!(unitPriceEl instanceof HTMLElement) || !value) return;

    if (!unitPriceEl.dataset.appstleOriginalUnitPrice) {
      unitPriceEl.dataset.appstleOriginalUnitPrice = this.#getUnitPriceVisibleText(unitPriceEl);
    }

    const hiddenLabel = unitPriceEl.querySelector('.visually-hidden');
    unitPriceEl.replaceChildren();
    if (hiddenLabel) {
      unitPriceEl.appendChild(hiddenLabel);
    }
    unitPriceEl.append(document.createTextNode(value));
  }

  /**
   * Restores the theme unit price after leaving subscription mode.
   * @param {Element} unitPriceEl
   */
  #restoreUnitPrice(unitPriceEl) {
    if (!(unitPriceEl instanceof HTMLElement)) return;

    const original = unitPriceEl.dataset.appstleOriginalUnitPrice;
    if (!original) return;

    const hiddenLabel = unitPriceEl.querySelector('.visually-hidden');
    unitPriceEl.replaceChildren();
    if (hiddenLabel) {
      unitPriceEl.appendChild(hiddenLabel);
    }
    unitPriceEl.append(document.createTextNode(original));
    delete unitPriceEl.dataset.appstleOriginalUnitPrice;
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  #formatPriceText(text) {
    const trimmed = text.trim();
    if (!trimmed) return trimmed;
    return trimmed.startsWith(' ') ? trimmed : ` ${trimmed}`;
  }
}

const appstlePriceSync = new AppstlePriceSync();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => appstlePriceSync.init(), { once: true });
} else {
  appstlePriceSync.init();
}
